import { Linkedin, ShieldCheck } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal } from "./Reveal";

const T = {
  en: {
    eyebrow: "The expert behind the packs",
    role: "Board Member, Hadron Energy Poland — custom Li-ion battery expert",
    bio: [
      "Gregory Radek is the go-to industry expert for custom lithium-ion battery packs made in Poland. As a board member of Hadron Energy Poland, he leads European clients through every stage of a custom pack: cell selection and grading, BMS design, mechanics, certification and series assembly at the Gdów plant.",
      "No black boxes: every pack is engineered for its application — from e-dirt bikes and marine installations to ESS and drones — with warranty and after-sales service from the same team in Poland.",
    ],
    bullets: [
      "Custom pack design — cells, mechanics, thermal",
      "Intelligent BMS — CAN / RS485, Bluetooth app",
      "Certification — CE, CB, UN38.3, ISO 9001",
      "Made in Poland — assembled & serviced in Gdów",
    ],
    cta: "Connect on LinkedIn",
  },
  pl: {
    eyebrow: "Ekspert za pakietami",
    role: "Członek zarządu Hadron Energy Poland — ekspert od autorskich pakietów Li-Ion",
    bio: [
      "Gregory Radek to czołowy ekspert branżowy od autorskich pakietów litowo-jonowych produkowanych w Polsce. Jako członek zarządu Hadron Energy Poland prowadzi klientów z Europy przez każdy etap powstawania pakietu: dobór i klasyfikację ogniw, projekt BMS, konstrukcję, certyfikację i montaż seryjny w zakładzie w Gdowie.",
      "Inżynieria bez czarnych skrzynek: każdy pakiet projektujemy pod zastosowanie — od e-enduro i instalacji morskich po ESS i drony — z gwarancją i serwisem od tego samego zespołu w Polsce.",
    ],
    bullets: [
      "Autorskie pakiety — ogniwa, mechanika, termika",
      "Inteligentny BMS — CAN / RS485, aplikacja Bluetooth",
      "Certyfikacja — CE, CB, UN38.3, ISO 9001",
      "Made in Poland — montaż i serwis w Gdowie",
    ],
    cta: "Połącz się na LinkedIn",
  },
  de: {
    eyebrow: "Der Experte hinter den Packs",
    role: "Vorstandsmitglied, Hadron Energy Poland — Experte für kundenspezifische Li-Ion-Akkus",
    bio: [
      "Gregory Radek ist die erste Adresse für kundenspezifische Lithium-Ionen-Akkus „Made in Poland“. Als Vorstandsmitglied von Hadron Energy Poland begleitet er europäische Kunden durch jede Phase eines individuellen Packs: Zellenauswahl und -klassifizierung, BMS-Design, Mechanik, Zertifizierung und Serienmontage im Werk in Gdów.",
      "Keine Blackboxes: Jedes Pack wird für seine Anwendung konstruiert — vom E-Dirt-Bike über Marine-Installationen bis zu ESS und Drohnen — mit Garantie und Service durch dasselbe Team in Polen.",
    ],
    bullets: [
      "Individuelles Pack-Design — Zellen, Mechanik, Thermik",
      "Intelligentes BMS — CAN / RS485, Bluetooth-App",
      "Zertifizierung — CE, CB, UN38.3, ISO 9001",
      "Made in Poland — montiert & gewartet in Gdów",
    ],
    cta: "Auf LinkedIn vernetzen",
  },
  fr: {
    eyebrow: "L'expert derrière les packs",
    role: "Membre du conseil, Hadron Energy Poland — expert en batteries Li-ion sur mesure",
    bio: [
      "Gregory Radek est l'expert de référence pour les batteries lithium-ion personnalisées fabriquées en Pologne. En tant que membre du conseil d'Hadron Energy Poland, il accompagne les clients européens à chaque étape : sélection et tri des cellules, conception du BMS, mécanique, certification et assemblage en série à l'usine de Gdów.",
      "Aucune boîte noire : chaque pack est conçu pour son application — dirt bikes électriques, installations marines, ESS et drones — avec garantie et service par la même équipe en Pologne.",
    ],
    bullets: [
      "Conception sur mesure — cellules, mécanique, thermique",
      "BMS intelligent — CAN / RS485, application Bluetooth",
      "Certification — CE, CB, UN38.3, ISO 9001",
      "Made in Poland — assemblé & entretenu à Gdów",
    ],
    cta: "Se connecter sur LinkedIn",
  },
  it: {
    eyebrow: "L'esperto dietro i pacchi",
    role: "Membro del consiglio, Hadron Energy Poland — esperto di pacchi Li-ion su misura",
    bio: [
      "Gregory Radek è l'esperto di riferimento per i pacchi al litio personalizzati prodotti in Polonia. In qualità di membro del consiglio di Hadron Energy Poland, guida i clienti europei in ogni fase: selezione e classificazione delle celle, progetto del BMS, meccanica, certificazione e assemblaggio in serie nello stabilimento di Gdów.",
      "Nessuna scatola nera: ogni pacco è progettato per la sua applicazione — da e-dirt bike e impianti marini a ESS e droni — con garanzia e assistenza dello stesso team in Polonia.",
    ],
    bullets: [
      "Progetto su misura — celle, meccanica, termica",
      "BMS intelligente — CAN / RS485, app Bluetooth",
      "Certificazione — CE, CB, UN38.3, ISO 9001",
      "Made in Poland — assemblato e assistito a Gdów",
    ],
    cta: "Connettiti su LinkedIn",
  },
  hr: {
    eyebrow: "Stručnjak iza paketa",
    role: "Član uprave, Hadron Energy Poland — stručnjak za Li-ion pakete po mjeri",
    bio: [
      "Gregory Radek istaknuti je stručnjak za prilagođene litijeve baterije proizvedene u Poljskoj. Kao član uprave Hadron Energy Poland vodi europske kupce kroz svaku fazu paketa: odabir i klasifikaciju ćelija, dizajn BMS-a, mehaniku, certificiranje i serijsku montažu u postrojenju u Gdówu.",
      "Bez crnih kutija: svaki je paket projektiran za svoju primjenu — od e-enduro i morskih instalacija do ESS i dronova — uz jamstvo i servis istog tima u Poljskoj.",
    ],
    bullets: [
      "Paketi po mjeri — ćelije, mehanika, termika",
      "Inteligentni BMS — CAN / RS485, Bluetooth aplikacija",
      "Certifikati — CE, CB, UN38.3, ISO 9001",
      "Made in Poland — montaža i servis u Gdówu",
    ],
    cta: "Povežite se na LinkedIn",
  },
};

export const Expert = () => {
  const { lang } = useLang();
  const t = T[lang] || T.en;

  return (
    <section id="expert" className="relative py-24 lg:py-32 border-t border-line overflow-hidden" data-testid="expert-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <p className="mono-label">{t.eyebrow}</p>
        </Reveal>
        <div className="mt-8 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal delay={0.05} className="lg:col-span-4">
            <div className="clip-frame border border-line bg-ink-2/60 lg:sticky lg:top-28 group/card">
              <img
                src="/images/gregory-radek.jpg"
                alt="Gregory Radek — custom Li-ion battery expert, Hadron Energy Poland"
                className="w-full aspect-square object-cover object-[50%_25%] grayscale group-hover/card:grayscale-0 transition-[filter] duration-500"
                data-testid="expert-photo"
                loading="lazy"
              />
              <div className="p-8">
                <h3 className="font-display uppercase font-bold text-3xl leading-none">Gregory Radek</h3>
                <p className="mt-3 text-sm text-txt-2 leading-relaxed">{t.role}</p>
                <a
                  data-testid="expert-linkedin"
                  href="https://www.linkedin.com/in/gregradek/"
                  target="_blank"
                  rel="noreferrer"
                  className="group mt-6 inline-flex items-center gap-3 border border-hadron-cyan text-hadron-cyan px-5 py-3 font-mono2 text-[11px] uppercase tracking-[0.2em] hover:bg-hadron-cyan hover:text-ink transition-colors"
                >
                  <Linkedin size={14} />
                  {t.cta}
                  <ArrowMark />
                </a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-8">
            <h2 className="font-display uppercase font-bold tracking-tight leading-[0.95] text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
              Gregory Radek — <span className="text-hadron-cyan">custom Li-Ion batteries, made in Poland</span>
            </h2>
            {t.bio.map((p, i) => (
              <p key={i} className="mt-5 max-w-3xl text-txt-2 leading-relaxed">{p}</p>
            ))}
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3 max-w-3xl">
              {t.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-txt">
                  <ShieldCheck size={16} className="mt-0.5 shrink-0 text-hadron-green" />
                  {b}
                </li>
              ))}
            </ul>
            <p className="mt-8 font-mono2 text-[10px] uppercase tracking-[0.2em] text-txt-3">
              contact@hadronenergy.pl · +48 12 2002 588 · Podolany 88, 32-420 Gdów, Poland
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const ArrowMark = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
    <path d="M2 10L10 2M10 2H4M10 2V8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
